// Customer-facing display (CFD).
//
// Open this route in a second window/tab on the same machine as POS. It
// subscribes to the v2 cart over BroadcastChannel and renders the cart
// in real time as the cashier builds it.
//
// Layout mirrors the v1 customer-facing template
// (`templates/core/pos/display_waio.html`):
//   left  -- receipt panel (customer name, items list, totals)
//   right -- featured-product panel (Phase 2: needs show-product /
//            hide-product channel messages)
//
// No authentication: the CFD is a passive read-only mirror of whatever
// the POS broadcasts. The BroadcastChannel transport is same-origin only,
// so cross-window leakage isn't a concern.
//
// To open the customer display from POS:
//   /customer  (literal route)
//
// In a typical Windows wrapper deploy, the wrapper opens this URL in a
// second window pinned to the customer-facing monitor.

import { potencySuffix } from '@/lib/potency'
import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Gift, ImageOff, ShoppingBag } from 'lucide-react'
import confetti from 'canvas-confetti'
import { setupCfdSubscriber, useCfdState } from '@/lib/cartCfdState'
import type { FeaturedProductPayload } from '@/lib/cartChannel'
import {
  onSessionChannelMessage,
  requestSessionState,
} from '@/lib/sessionChannel'
import { onReloadSignal } from '@/lib/reloadChannel'
import { typeBadgeClasses } from '@/lib/productType'
import { formatCurrency } from '@/lib/utils'
import { splitCartDiscount } from '@/lib/cart'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { useSessionStore } from '@/stores/sessionStore'
import { usePosBootstrap, bootstrapKeys } from '@/api/bootstrap'

// Strain values the catalog uses as "no strain" sentinels — same predicate
// the POS-side ProductCard / CartItemRow use. Suppresses literal "-",
// "N/A", "none", and empty strings from the strain pill.
function isMeaningfulStrain(strain: string | null | undefined): strain is string {
  if (typeof strain !== 'string' || !strain) return false
  const n = strain.trim().toLowerCase()
  return n !== '' && n !== '-' && n !== 'n/a' && n !== 'none'
}

function strainBadgeClasses(strain: string): string {
  const lower = strain.toLowerCase()
  if (lower.includes('sativa')) return 'bg-orange-100 text-orange-700'
  if (lower.includes('indica')) return 'bg-purple-100 text-purple-700'
  if (lower.includes('hybrid')) return 'bg-green-100 text-green-700'
  return 'bg-gray-100 text-gray-700'
}

function parseNum(v: string | null): number | null {
  if (v === null) return null
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : null
}

// Mirror posStore.calculateLineTotal for use when /cart/preview hasn't
// (or can't) priced the line. Discount magnitude reduces the line toward
// zero in both directions: refund qty < 0 flips the sign so a $10 discount
// on a -1 refund line yields -$37.49 (not -$57.49).
function localLineTotal(line: {
  qty: number
  display: { estimated_price: number }
  discount_amount?: number | null
}): number {
  const gross = line.display.estimated_price * line.qty
  const discount = line.discount_amount ?? 0
  if (!discount) return gross
  return gross - (line.qty < 0 ? -discount : discount)
}

export const Route = createFileRoute('/customer')({
  component: CustomerDisplay,
})

// const STALE_MS = 30_000
// const STALE_TICK_MS = 5_000 // re-evaluate "isStale" every 5s

function CustomerDisplay() {
  const { snapshot, featuredProduct, hasConnected, paymentApprovedTick } = useCfdState()
  const queryClient = useQueryClient()

  // Wire the channel subscription on mount. The teardown handles HMR /
  // navigation cleanly so the singleton transport doesn't accumulate
  // duplicate listeners.
  useEffect(() => {
    const off = setupCfdSubscriber()
    return () => off()
  }, [])

  // Reload in lockstep with POS when staff applies an app update. Without
  // this, POS reloads to new code while the CFD keeps running old code —
  // wire-format drift on cart/session channels is silent and surfaces as
  // a stuck or blank customer display. CFD is a passive read-only mirror
  // so reloading is always safe; no in-flight cart/modal/mutation state
  // to worry about.
  useEffect(() => {
    const off = onReloadSignal(() => {
      window.location.reload()
    })
    return () => off()
  }, [])

  // One-shot confetti when POS broadcasts payment.approved (full receipt
  // close, never partials, never refunds — see broadcastPaymentApproved
  // in cartChannel.ts). Two cannons firing alternately from bottom corners
  // for ~2.5s. Skip the initial mount tick (0) so a fresh CFD load doesn't
  // celebrate nothing.
  //
  // `useWorker: false` is REQUIRED for prod. canvas-confetti can run its
  // render loop in a Web Worker spawned from a `blob:` URL, which is blocked
  // by our CSP (vercel.json — `default-src 'self'`, no `worker-src blob:`).
  // We configure it via `confetti.create(..., { useWorker: false })`.
  //
  // NOTE: `useWorker` is a *global* canvas-confetti option, not a per-burst
  // shot option.
  //
  // canvas-confetti defaults to
  // running its render loop in a Web Worker spawned from a `blob:` URL,
  // which is blocked by our CSP (vercel.json — `default-src 'self'`, no
  // `worker-src blob:`). The library swallows the worker construction
  // error and silently no-ops, so confetti works in dev (no CSP) but
  // fails silently in prod. Running on the main thread is fine for the
  // particle count we use here.
  useEffect(() => {
    if (paymentApprovedTick === 0) return
    const fireConfetti = confetti.create(undefined, {
      resize: true,
      useWorker: false,
    })
    const duration = 2500
    const end = Date.now() + duration
    const colors = ['#22c55e', '#10b981', '#f59e0b', '#fbbf24', '#ffffff']
    const tick = () => {
      const remaining = end - Date.now()
      if (remaining <= 0) return
      fireConfetti({
        particleCount: 4,
        angle: 60,
        spread: 65,
        startVelocity: 55,
        origin: { x: 0, y: 0.85 },
        colors,
      })
      fireConfetti({
        particleCount: 4,
        angle: 120,
        spread: 65,
        startVelocity: 55,
        origin: { x: 1, y: 0.85 },
        colors,
      })
      requestAnimationFrame(tick)
    }
    tick()
  }, [paymentApprovedTick])

  // Cross-window session sync (additive only). POS publishes a snapshot
  // ONLY when fully formed, so any message we receive here is safe to
  // apply. Three fields total — no isAuthenticated, no staff, no
  // backendUrl — to make sure no field touched here can trigger a route
  // guard. There is no `session.cleared` handler: logout never broadcasts,
  // so the CFD's view never goes down. Worst case after POS logout: a
  // stale logo URL until the next login, which is invisible to the user.
  //
  // Also handles `session.bootstrap_invalidated`: POS fires this when a
  // clerk PIN-in succeeds. We invalidate the local bootstrap query so
  // `usePosBootstrap` refetches against this window's own queryClient.
  // (POS and CFD are separate React roots → separate queryClients →
  // invalidating in POS doesn't reach here, hence the broadcast.)
  useEffect(() => {
    const off = onSessionChannelMessage((msg) => {
      if (msg.type === 'session.updated' && msg.session) {
        const s = msg.session
        useSessionStore.setState({
          backendUrl: s.backendUrl,
          sessionToken: s.sessionToken,
          selectedStore: s.selectedStore,
          selectedRegister: s.selectedRegister,
          displayLogo: s.displayLogo,
        })
        return
      }
      if (msg.type === 'session.bootstrap_invalidated') {
        queryClient.invalidateQueries({ queryKey: bootstrapKeys.all })
      }
    })
    requestSessionState()
    return () => off()
  }, [queryClient])

  // Stale-link indicator is intentionally disabled for now.
  // const [nowTick, setNowTick] = useState(() => Date.now())
  // useEffect(() => {
  //   const id = window.setInterval(
  //     () => setNowTick(Date.now()),
  //     STALE_TICK_MS,
  //   )
  //   return () => window.clearInterval(id)
  // }, [])
  // const isStale =
  //   lastReceivedAt !== null && nowTick - lastReceivedAt > STALE_MS

  const hasCart = !!snapshot && snapshot.lines.length > 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-6">
      <div className="mx-auto max-w-8xl grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ---- LEFT: receipt panel ----
            Fixed height (not min-h) so the inner flex layout can scroll
            its items list independently and keep the totals bar pinned
            to the bottom even with 10+ items in the cart. */}
        <div className="lg:col-span-6 bg-white rounded-2xl shadow-sm p-6 md:p-8 md:pt-6 h-[93vh]">
          {!hasCart ? (
            <EmptyReceipt hasConnected={hasConnected} />
          ) : (
            <ReceiptPanel snapshot={snapshot!} />
          )}
        </div>

        {/* ---- RIGHT: featured product / welcome panel ----
            Matches the receipt panel's fixed height so both columns
            stay aligned. `overflow-y-auto` lets long product
            descriptions scroll within the panel rather than blowing
            out the viewport. */}
        <div className="lg:col-span-6 bg-white rounded-2xl shadow-sm p-6 md:p-8 h-[93vh] flex flex-col overflow-y-auto">
          {featuredProduct
            ? <FeaturedProductPanel product={featuredProduct} />
            : <WelcomeRightPanel />
          }
        </div>
      </div>

      {/* {isStale && (
        <div className="fixed bottom-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          Connection idle
        </div>
      )} */}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

// Welcome-state right panel — shown when no transaction is active and
// no product is featured on POS. Two layers, in order:
//   1. /pos/bootstrap's `display_logo` (full-hierarchy, freshest server
//      resolution). Available once the CFD has session token + store
//      + register, which lands via the session-channel broadcast.
//   2. sessionStore.displayLogo (mirrored from POS via the same
//      broadcast). Set as soon as the auth response lands — i.e.
//      BEFORE bootstrap resolves, and survives a CFD reload via the
//      persisted store. This is what lets the brand logo paint the
//      moment the cashier finishes login, instead of waiting for the
//      first cart action to nudge the bootstrap query.
function useCompanyLogoUrl(): string | null {
  const sessionToken = useSessionStore((s) => s.sessionToken)
  const clerkToken = useSessionStore((s) => s.clerkToken)
  const selectedStore = useSessionStore((s) => s.selectedStore)
  const selectedRegister = useSessionStore((s) => s.selectedRegister)
  const fallbackLogo = useSessionStore((s) => s.displayLogo)

  // `v2()` (called inside usePosBootstrap) throws if store/register
  // aren't selected yet — gate enabled until the session is ready so
  // the CFD can mount before POS logs in without exploding.
  //
  // `clerkToken` is part of that gate because /pos/bootstrap is a
  // CLERK-scoped endpoint: the BE's _scope() rejects it with
  // `403 v2 requires X-POS-Clerk-Token` when the header is absent, and
  // the header is only attached when a clerkToken exists (api/client
  // request interceptor). The CFD's clerkToken comes from ONE place --
  // the persisted store rehydrating at page load -- because
  // sessionChannel's SessionSnapshot deliberately omits it (adding it
  // would let a cross-window setState trip the route guards). So a CFD
  // opened before the cashier PINs in has no clerk token and no way to
  // acquire one while it stays open.
  //
  // Without this gate that CFD re-fired a guaranteed-403 bootstrap every
  // refreshMs for the life of the window, and each 403 dragged a futile
  // session refresh through the response interceptor (which retries 403
  // like 401 -- but refreshing a SESSION token can never supply a CLERK
  // token, so it cannot converge). Fleet-wide that was ~20 403s/min,
  // ~29k/day, steady for as long as the portal logs go back.
  //
  // Nothing is lost visually: layer 2 below (sessionStore.displayLogo,
  // mirrored from POS via the session channel) is the designed fallback
  // and is already what paints the logo before bootstrap resolves.
  const { data } = usePosBootstrap({
    enabled: !!sessionToken && !!clerkToken && !!selectedStore?.id && !!selectedRegister?.id,
  })

  const fromBootstrap = typeof data?.display_logo === 'string' ? data.display_logo.trim() : ''
  if (fromBootstrap) return fromBootstrap
  const fromSession = typeof fallbackLogo === 'string' ? fallbackLogo.trim() : ''
  return fromSession || null
}

function WelcomeRightPanel() {
  const logoUrl = useCompanyLogoUrl()
  const [logoFailed, setLogoFailed] = useState(false)
  // Reset failure flag whenever the URL changes (e.g. register switch
  // resolves a different store) so a stale 404 doesn't suppress a fresh
  // logo.
  useEffect(() => {
    setLogoFailed(false)
  }, [logoUrl])
  const showFallback = !logoUrl || logoFailed
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center">
      {showFallback ? (
        <div className="w-48 h-48 md:w-64 md:h-64 rounded-2xl bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
          <ShoppingBag size={80} className="text-green-500" strokeWidth={1.5} />
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={logoUrl!}
          alt=""
          className="max-w-[30%] max-h-[40vh] object-contain"
          onError={() => setLogoFailed(true)}
        />
      )}
    </div>
  )
}

// Featured-product panel — mirrors the legacy display_waio.html layout:
// hero image, brand + name, strain pill, price block, weight chip, and
// description. Renders whatever pricing the POS sent over the channel; no
// tier-decision logic lives here so the CFD stays a passive read-only
// mirror.
function FeaturedProductPanel({ product }: { product: FeaturedProductPayload }) {
  const effectivePrice = parseNum(product.effective_unit_price)
  const regular = parseNum(product.regular_price)

  // Show only what the customer is actually paying — no strikethroughs,
  // no tier/sale price reveals on the customer screen. POS pre-resolved
  // the effective unit price (server-priced when in cart, FE-computed
  // via effectiveUnitPrice otherwise) so we render it verbatim. The
  // regular_price fallback is defensive only — POS always supplies an
  // effective price in practice.
  const primary =
    effectivePrice !== null && effectivePrice > 0
      ? effectivePrice
      : regular

  const unitName = product.unit_name
  const showWeightChip = !!unitName && primary !== null

  // THC / CBD pills — render alongside the strain pill when the catalog
  // carries a percentage. Mirrors the legacy display_waio.html chip row.
  const thc = parseNum(product.test_thc)
  const cbd = parseNum(product.test_cbd)

  return (
    <div className="flex-1 flex flex-col">
      {/* Top: hero image + headline (brand, name, strain, price).
          5-col grid -> image 2/5 (40%), text 3/5 (60%). Stacks on mobile. */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <div className="md:col-span-2 aspect-square rounded-2xl bg-gray-50 flex items-center justify-center overflow-hidden">
          {product.image ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img
              src={withCdnImageWidth(product.image, 350)}
              alt=""
              className="w-full h-full object-contain"
            />
          ) : (
            <ImageOff size={64} className="text-gray-300" strokeWidth={1.5} />
          )}
        </div>

        <div className="md:col-span-3 flex flex-col justify-center">
          {product.brand_name && (
            <p className="text-xs text-gray-500 mb-1">{product.brand_name}</p>
          )}
          <h2 className="text-md md:text-md font-semibold text-gray-900 leading-tight">
            {product.description}
          </h2>

          {(product.product_type_name || isMeaningfulStrain(product.strain) || thc !== null || cbd !== null) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {/* Strain classification (Sativa / Indica / Hybrid / Blend)
                  with the same color treatment as the POS product card.
                  Always renders first so it anchors the chip row left. */}
              {product.product_type_name && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${typeBadgeClasses(product.product_type_name)}`}
                >
                  {product.product_type_name}
                </span>
              )}
              {isMeaningfulStrain(product.strain) && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${strainBadgeClasses(product.strain)}`}
                >
                  {product.strain}
                </span>
              )}
              {thc !== null && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                  THC: {thc}{potencySuffix(product.test_units)}
                </span>
              )}
              {cbd !== null && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                  CBD: {cbd}{potencySuffix(product.test_units)}
                </span>
              )}
            </div>
          )}

          {/* Customer-facing price — single value, no strikethrough. */}
          {primary !== null && (
            <div className="mt-5 flex items-baseline gap-3">
              <span className="text-xl md:text-xl font-bold text-gray-900 tabular-nums">
                {formatCurrency(primary)}
                {unitName && (
                  <span className="text-base font-medium text-gray-500 ml-1">
                    {' / '}{unitName}
                  </span>
                )}
              </span>
            </div>
          )}

          {showWeightChip && (
            <div className="mt-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-green-300 bg-green-50 text-sm font-semibold text-green-800">
                <span>{unitName}</span>
                <span className="text-green-600 font-bold">
                  {formatCurrency(primary!)}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Description — the long marketing paragraph from
          `product_info_text`, NOT the wire's `description` field
          (which is the title we already rendered above). When the
          catalog doesn't carry marketing copy, hide the block
          entirely rather than repeating the title. */}
      {product.product_info_text && (
        <div className="border-t border-gray-100 pt-6 mt-2">
          <h3 className="text-md font-semibold text-gray-900 mb-3">Description</h3>
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
            {product.product_info_text}
          </p>
        </div>
      )}
    </div>
  )
}

function EmptyReceipt({ hasConnected }: { hasConnected: boolean }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center">
      {hasConnected ? (
        <h1 className="text-3xl md:text-3xl font-semibold text-gray-900">
          Welcome!
        </h1>
      ) : (
        <>
          <div className="text-gray-300 text-7xl mb-6">·</div>
          <p className="text-gray-500 text-lg">Waiting for register…</p>
        </>
      )}
    </div>
  )
}

function ReceiptPanel({ snapshot }: { snapshot: NonNullable<ReturnType<typeof useCfdState>['snapshot']> }) {
  const customer = snapshot.customer
  const totals = snapshot.totals
  const itemCount = snapshot.lines.reduce((n, l) => n + Math.abs(l.qty), 0)

  // Numbers come from the BE preview when available. Until the first
  // preview returns, we render local subtotal so the cashier doesn't
  // think the cart is broken (mirrors POS-side behaviour in CartSummary).
  // Mirror posStore.calculateLineTotal: subtract the cashier's per-line
  // discount with the sign flipped on refund qty so it always pulls the
  // line toward zero.
  const localSubtotal = snapshot.lines.reduce(
    (sum, l) => sum + localLineTotal(l),
    0,
  )
  const subtotal = totals !== null
    ? parseFloat(totals.subtotal) || 0
    : localSubtotal
  const taxes = totals !== null ? parseFloat(totals.taxes) || 0 : 0
  const deposit = totals !== null
    ? parseFloat(totals.deposit_amount ?? '0') || 0
    : 0
  // discount_amount is every line discount. When a SpringBig percent offer
  // rides the cart it stacks on sale/aged discounts, so split it: the offer's
  // own value matches the cashier's "Loyalty applied" figure, and the rest
  // shows as Savings. The two rows sum to the same total as before.
  const { savings: discount, loyalty: loyaltyDiscount } = splitCartDiscount(totals)
  const total = totals !== null
    ? parseFloat(totals.total) || 0
    : localSubtotal

  // Staged loyalty redemption pushed from the cashier's CustomerHeader. Mirrors
  // the CheckoutModal "Loyalty −$X / To collect" rows so the customer sees the
  // same reduction the cashier sees while a reward/offer is staged. The
  // `available` flag drives the "Rewards Available" badge in the header.
  const loyalty = snapshot.loyalty
  const stagedLoyalty = loyalty?.stagedAmount ?? 0
  const toCollect = Math.max(total - stagedLoyalty, 0)

  // Partial-payment context — only present when CheckoutModal has pushed
  // a receipt response with at least one tendered payment. Mirrors the
  // legacy v1 template's `payments_dict` rows.
  const payments = snapshot.partialPayments ?? []
  const remainingBalance = snapshot.partialAmountToPay !== null
    ? parseFloat(snapshot.partialAmountToPay) || 0
    : null
  const hasPartials = payments.length > 0

  return (
    <div className="flex flex-col h-full">
      {/* Header: greeting + item count */}
      <div className="flex items-baseline justify-between mb-1 pb-2 border-b border-gray-100">
        <div>
          <p className="text-gray-500 text-sm">Welcome back,</p>
          <div className="flex items-center gap-2 mt-0">
            <h1 className="text-md md:text-md font-semibold text-gray-900">
              {customerGreeting(customer)}
            </h1>
            {loyalty?.available && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-semibold">
                <Gift size={12} />
                Rewards Available
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-sm text-gray-400">
          {itemCount > 0 && (
            <span>
              {itemCount} item{itemCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto">
        {snapshot.lines.map((line) => (
          <CartLineRow key={line.localId} line={line} />
        ))}
      </div>

      {/* Totals — two-column layout: payments always on the left,
          order totals always on the right. The Remaining Balance row
          lives under the payments list (left) so the customer's eye
          tracks "Cash -$20 → still owe $X" naturally. Item count is
          already shown in the header, so we don't repeat it here. */}
      <div className="mt-3 pt-4 border-t border-gray-100 grid grid-cols-2 gap-x-8 text-sm">
        {/* Left: payments + remaining balance */}
        <div className="space-y-1.5">
          {payments.map((p, i) => (
            <SummaryRow
              key={`pay-${i}`}
              label={
                Number(p.tip_amount ?? 0) > 0.005
                  ? `${p.payment_method ?? 'Payment'} (incl. ${formatCurrency(Number(p.tip_amount ?? 0))} tip)`
                  : (p.payment_method ?? 'Payment')
              }
              value={`-${formatCurrency(Math.abs(parseFloat(p.amount) || 0))}`}
              className="text-red-600"
            />
          ))}
          {hasPartials && remainingBalance !== null && remainingBalance > 0 && (
            <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-gray-100">
              <span className="text-gray-900 font-semibold">
                Remaining Balance
              </span>
              <span className="text-base font-bold text-amber-600 tabular-nums">
                {formatCurrency(remainingBalance)}
              </span>
            </div>
          )}
        </div>

        {/* Right: order totals */}
        <div className="space-y-1.5">
          <SummaryRow label="Subtotal" value={formatCurrency(subtotal)} />
          {deposit !== 0 && (
            <SummaryRow label="Deposit" value={formatCurrency(deposit)} />
          )}
          {discount !== 0 && (
            <SummaryRow
              label={loyaltyDiscount !== null && loyaltyDiscount > 0 ? 'Savings' : 'Discount'}
              value={`-${formatCurrency(discount)}`}
              className="text-green-600"
            />
          )}
          {loyaltyDiscount !== null && loyaltyDiscount > 0 && (
            <SummaryRow
              label="Loyalty"
              value={`-${formatCurrency(loyaltyDiscount)}`}
              className="text-green-600"
            />
          )}
          {taxes !== 0 && (
            <SummaryRow label="Taxes" value={formatCurrency(taxes)} />
          )}
          <div className="flex justify-between items-baseline">
            <span className="text-gray-900 font-medium">Total</span>
            <span className={`text-lg font-bold tabular-nums ${total < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(total)}
            </span>
          </div>
          {stagedLoyalty > 0 && (
            <>
              <SummaryRow
                label="Loyalty"
                value={`-${formatCurrency(stagedLoyalty)}`}
                className="text-green-600"
              />
              <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-gray-100">
                <span className="text-gray-900 font-semibold">To collect</span>
                <span className="text-lg font-bold tabular-nums text-gray-900">
                  {formatCurrency(toCollect)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function CartLineRow({
  line,
}: {
  line: NonNullable<ReturnType<typeof useCfdState>['snapshot']>['lines'][number]
}) {
  // Pre-tax line subtotal — matches the cashier's cart and the Subtotal
  // row at the bottom of the receipt panel. Using `priced.total` here
  // would include tax + deposit, which surfaces double-counting once the
  // tax row renders below.
  //
  // priced.subtotal is already post-discount when /cart/preview ran with
  // the discount in the body. The local fallback (preview failed or not
  // yet returned) needs to subtract the cashier discount manually so the
  // CFD doesn't show the pre-discount catalog price.
  const lineTotal = line.priced
    ? parseFloat(line.priced.subtotal) || 0
    : localLineTotal(line)

  // Sale strikethrough: only show the original price next to the line
  // total when the BE flagged the line on sale AND compare > paid. The
  // cashier's per-line discount is conveyed via the discount row at the
  // bottom of the receipt, not duplicated here.
  const compare = line.display.compare_price ?? null
  const unitPaid = line.qty !== 0 ? lineTotal / line.qty : 0
  const showSale =
    line.display.on_sale === true
    && compare !== null
    && compare > Math.abs(unitPaid)

  // Brand · UOM subtitle line (replaces the Lot row the cashier sees but
  // the customer doesn't care about). Falls back to either field alone if
  // the other is missing; renders nothing when neither exists.
  const subtitleParts: string[] = []
  if (line.display.brand_name) subtitleParts.push(line.display.brand_name)
  if (line.display.unit_name) subtitleParts.push(line.display.unit_name)
  const subtitle = subtitleParts.join(' · ')

  return (
    <div className="flex items-center gap-4 py-2 border-b border-gray-50 last:border-b-0">
      {line.display.image ? (
        <img
          src={withCdnImageWidth(line.display.image, 100)}
          alt=""
          className="w-14 h-14 rounded-lg object-cover bg-gray-100 flex-shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {line.display.description || `Product ${line.product_id}`}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {subtitle && (
            <p className="text-xs text-gray-500 truncate">{subtitle}</p>
          )}
          {showSale && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 uppercase tracking-wide">
              Sale
            </span>
          )}
        </div>
      </div>
      <div className="text-sm text-gray-500 w-12 text-center">
        ×{line.qty}
      </div>
      <div className="text-right w-24 text-sm font-medium text-gray-900">
        <div className="tabular-nums">{formatCurrency(lineTotal)}</div>
        {showSale && (
          <div className="text-xs text-gray-400 line-through tabular-nums">
            {formatCurrency(compare! * Math.abs(line.qty))}
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryRow({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={`flex justify-between ${className ?? 'text-gray-500'}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function customerGreeting(
  customer: NonNullable<ReturnType<typeof useCfdState>['snapshot']>['customer'],
): string {
  if (!customer) return 'Retail Customer'
  // First name + last initial, falling back to display_name. Matches the
  // privacy-conscious format used in the v1 template.
  const first = customer.first_name?.trim()
  const last = customer.last_name?.trim()
  if (first && last) return `${first} ${last.charAt(0).toUpperCase()}.`
  if (customer.display_name) return customer.display_name
  if (first) return first
  return 'Customer'
}
